type PublicEnvName =
  | "NODE_ENV"
  | "BUN_PUBLIC_CONVEX_URL"
  | "CONVEX_URL"
  | "BUN_PUBLIC_CONVEX_SITE_URL"
  | "CONVEX_SITE_URL"
  | "NEXT_PUBLIC_CONVEX_SITE_URL"
  | "BUN_PUBLIC_SITE_URL"
  | "SITE_URL"
  | "BUN_PUBLIC_INTERNAL_SEARCH_V2"
  | "BUN_PUBLIC_SALES_CREDIT_COMPARISON";

type RuntimePublicEnv = Partial<Record<PublicEnvName, string>>;

type GlobalWithAppEnv = typeof globalThis & {
  __APP_ENV__?: RuntimePublicEnv;
};

function normalizeEnvValue(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : undefined;
}

function readWindowEnv(name: PublicEnvName): string | undefined {
  const appEnv = (globalThis as GlobalWithAppEnv).__APP_ENV__;
  if (!appEnv) return undefined;
  return normalizeEnvValue(appEnv[name]);
}

function readProcessEnv(name: PublicEnvName): string | undefined {
  if (typeof process === "undefined") return undefined;
  return normalizeEnvValue(process.env[name]);
}

function readPublicEnv(name: PublicEnvName): string | undefined {
  return readWindowEnv(name) ?? readProcessEnv(name);
}

function parseBooleanEnv(value: string | undefined): boolean | undefined {
  if (value === undefined) return undefined;
  const normalized = value.trim().toLowerCase();
  if (["1", "true", "yes", "on"].includes(normalized)) return true;
  if (["0", "false", "no", "off"].includes(normalized)) return false;
  return undefined;
}

const NODE_ENV = readPublicEnv("NODE_ENV") ?? "development";
const IS_PROD = NODE_ENV === "production";

const convexUrl =
  readPublicEnv("BUN_PUBLIC_CONVEX_URL") ??
  readPublicEnv("CONVEX_URL") ??
  (IS_PROD ? undefined : "http://localhost:3210");
const convexSiteUrl =
  readPublicEnv("BUN_PUBLIC_CONVEX_SITE_URL") ??
  readPublicEnv("CONVEX_SITE_URL") ??
  readPublicEnv("NEXT_PUBLIC_CONVEX_SITE_URL") ??
  (IS_PROD ? undefined : "http://localhost:3211");
const siteUrl =
  readPublicEnv("BUN_PUBLIC_SITE_URL") ??
  readPublicEnv("SITE_URL") ??
  (IS_PROD ? undefined : "http://localhost:5178");
const internalSearchV2 =
  parseBooleanEnv(readPublicEnv("BUN_PUBLIC_INTERNAL_SEARCH_V2")) ?? !IS_PROD;

if (IS_PROD && (!convexUrl || !convexSiteUrl)) {
  throw new Error(
    "Missing public Convex configuration. Set BUN_PUBLIC_CONVEX_URL and BUN_PUBLIC_CONVEX_SITE_URL."
  );
}

export const ENV = {
  NODE_ENV,
  IS_DEV: NODE_ENV !== "production",
  CONVEX_URL: convexUrl ?? "",
  CONVEX_SITE_URL: convexSiteUrl ?? "",
  SITE_URL: siteUrl ?? "",
  INTERNAL_SEARCH_V2: internalSearchV2,
  SALES_CREDIT_COMPARISON: parseBooleanEnv(readPublicEnv("BUN_PUBLIC_SALES_CREDIT_COMPARISON")) === true,
} as const;
